import React, { useCallback, useEffect, useRef, useState } from 'react';

import { RequirementSyncReview } from '../RequirementSyncReview';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { formatDate } from '../../utils/dateUtils';
import {
  CAB_TO_TICKETS,
  TICKETS_TO_CAB,
  applySyncJob,
  cancelSyncJob,
  getSyncJob,
  isSyncReviewOpen,
  rejectAllDecisions,
  getSyncStatus,
  startSyncJob,
  syncErrorMessage,
  type SyncChangeCounts,
  type SyncDecision,
  type SyncJob,
  type SyncRuleStatus,
} from '../../services/requirementSyncApi';
import { plural, syncAppliedAnything, syncAppliedText, syncDiscardedText } from './syncText';

import type { IRequirementSyncBannerProps } from './RequirementSyncBanner.types';

const RUNNING = ['queued', 'running'];
// How long "No other tickets affected" stays before it goes away by itself.
const FADE_MS = 6000;

/** Still working: queued or running, or an impact check recording that it found nothing to change. */
const isRunning = (j: SyncJob) => RUNNING.includes(j.status) || (j.followupOf != null && j.status === 'applying');

/** "2 tickets edited, 1 new, 1 deleted" — the first count names what is counted. */
function describeCounts(c: SyncChangeCounts, noun: string): string {
  return ([['modified', 'edited'], ['created', noun === 'ticket' ? 'new' : 'added'], ['deleted', noun === 'ticket' ? 'deleted' : 'removed']] as const)
    .filter(([key]) => c[key])
    .map(([key, verb], i) => (i > 0 ? `${c[key]} ${verb}`
      : key === 'created' && noun === 'ticket' ? plural(c[key], 'new ticket') : `${plural(c[key], noun)} ${verb}`))
    .join(', ');
}

/** Wording per direction; the CAB is updated from tickets first, then tickets follow the CAB. */
const TEXT: Record<string, {
  pending: string;
  start: string;
  running: string;
  ready: (n: number) => string;
  none: string;
  failed: string;
}> = {
  [TICKETS_TO_CAB]: {
    pending: 'Tickets changed since the CAB was last updated from them',
    start: 'Update CAB',
    running: 'Working out what these ticket changes mean for the CAB…',
    ready: n => `${plural(n, 'CAB update')} ready to review`,
    none: 'The CAB already reflects these ticket changes',
    failed: "Couldn't update the CAB from the tickets",
  },
  [CAB_TO_TICKETS]: {
    pending: 'The CAB changed since the tickets were last updated from it',
    start: 'Check impact',
    running: 'Checking which tickets are affected by the CAB changes…',
    ready: n => `${plural(n, 'ticket update')} ready to review`,
    none: 'Tickets are already up to date with the CAB',
    failed: "Couldn't check ticket impact",
  },
};

/** "TKT-120, TKT-133 and 1 new ticket": the tickets an impact check would change. */
function affectedTickets(job: SyncJob): string {
  const proposals = job.result?.proposals ?? [];
  const ids = Array.from(new Set(proposals.filter(p => p.action !== 'create' && p.itemId).map(p => p.itemId as string)))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const created = proposals.filter(p => p.action === 'create').length;
  // Up to five IDs, then "N more"; one "and" before the last part.
  const shown = ids.length > 5 ? ids.slice(0, 4) : ids;
  const parts = [...shown, ...(ids.length > shown.length ? [`${ids.length - shown.length} more`] : []),
    ...(created ? [plural(created, 'new ticket')] : [])];
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] ?? '';
}

/** The impact check that starts by itself once ticket edits changed the CAB: one flow with the edit that caused it. */
const FOLLOWUP = {
  running: 'Checking whether other tickets are affected…',
  ready: (job: SyncJob) => (job.result?.proposals ?? []).every(p => p.action === 'create')
    ? `Your change also needs ${affectedTickets(job)}`
    : `Your change also affects ${affectedTickets(job)}`,
  none: 'No other tickets affected',
  failed: "Couldn't check whether other tickets are affected",
};

type Tone = 'info' | 'error' | 'success';

// Brand fg tokens are chartreuse and vanish on the brand-tinted bar, so info uses brand-800.
const TONES: Record<Tone, { bar: string; icon: IconName; iconColor: string }> = {
  info: { bar: 'border-border-primary bg-bg-brand-primary-alt', icon: IconName.InfoCircle, iconColor: 'text-utility-brand-800' },
  error: { bar: 'border-border-error bg-bg-error-primary', icon: IconName.Alert, iconColor: 'text-fg-error-primary' },
  success: { bar: 'border-border-primary bg-bg-success-primary', icon: IconName.CheckCircle, iconColor: 'text-fg-success-primary' },
};

const Bar: React.FC<{
  tone: Tone;
  spinning?: boolean;
  title: string;
  details?: string[];
  children?: React.ReactNode;
}> = ({ tone, spinning = false, title, details = [], children }) => {
  const { bar, icon, iconColor } = TONES[tone];
  return (
    <div className={`flex items-center gap-bg-v2-md px-bg-v2-xl py-bg-v2-md border-b flex-shrink-0 ${bar}`}>
      <div className="flex items-start gap-bg-v2-md flex-1 min-w-0">
        {/* One title line tall, so the icon centres on the title however many detail lines follow. */}
        <div className="flex items-center h-[var(--line-height-text-sm)] flex-shrink-0">
          {spinning ? (
            <div className="w-4 h-4 border-2 border-border-secondary border-t-utility-brand-800 rounded-full animate-spin" />
          ) : (
            <Icon name={icon} size="sm" inheritColor className={iconColor} />
          )}
        </div>
        <div className="flex flex-col min-w-0">
          <Text variant="text-sm" weight="semibold" color="primary">{title}</Text>
          {details.map(line => (
            <Text key={line} variant="text-sm" color="secondary">{line}</Text>
          ))}
        </div>
      </div>
      {children && <div className="flex items-center gap-bg-v2-sm flex-shrink-0">{children}</div>}
    </div>
  );
};

export const RequirementSyncBanner: React.FC<IRequirementSyncBannerProps> = ({
  projectId,
  canEdit,
  reloadSignal = 0,
  onApplied,
  onReviewInline,
  autoReview = false,
  onAutoReviewDone,
  onReviewWaitingChange,
}) => {
  const [rules, setRules] = useState<SyncRuleStatus[]>([]);
  // The first status read finished (with or without an error).
  const [loaded, setLoaded] = useState(false);
  const [job, setJob] = useState<SyncJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What the last apply or discard did; neutral when it changed nothing.
  const [notice, setNotice] = useState<{ text: string; tone: Tone } | null>(null);
  // A notice that goes away by itself ("No other tickets affected").
  const [fading, setFading] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Only while the CAB page prepares its inline review: busy also covers the pop-up's apply and discard.
  const [opening, setOpening] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const next = await getSyncStatus(projectId);
      setRules(next);
      setJob(next.find(r => r.job)?.job ?? null);
    } catch (err) {
      console.error('Requirement sync status failed:', err);
    } finally {
      setLoaded(true);
    }
  }, [projectId]);

  useEffect(() => { loadStatus(); }, [loadStatus, reloadSignal]);

  const jobId = job?.jobId;
  const jobRunning = job ? isRunning(job) : false;
  useEffect(() => {
    if (!jobId || !jobRunning) return;
    const timer = setInterval(async () => {
      try {
        const next = await getSyncJob(projectId, jobId);
        if (next.autoSettled) {
          // The impact check found no ticket to change and recorded that itself: say so briefly, nothing to do.
          setJob(null);
          setFading(FOLLOWUP.none);
          loadStatus();
        } else if (!isRunning(next)) {
          setJob(next);
          // A failed job is no longer open, so the status call would drop it: keep it on screen with its
          // error and "Try again", and refresh only the rules.
          if (next.status === 'cancelled') loadStatus();
          else if (next.status === 'failed') getSyncStatus(projectId).then(setRules).catch(() => {});
        }
      } catch {
        // try again on the next tick
      }
    }, (job?.pollIntervalSec ?? 5) * 1000);
    return () => clearInterval(timer);
  }, [projectId, jobId, jobRunning, job?.pollIntervalSec, loadStatus]);

  useEffect(() => {
    if (!fading) return;
    const timer = setTimeout(() => setFading(null), FADE_MS);
    return () => clearTimeout(timer);
  }, [fading]);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(syncErrorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  // The rule to offer next: ticket edits go to the CAB before the CAB updates tickets.
  const pending = rules.find(r => r.state === 'changes_pending' && !r.blockedBy) ?? null;
  // Ticket edits waiting while the CAB is busy (a generation, or a CAB change awaiting review).
  const waiting = !pending
    ? rules.find(r => r.state === 'busy' && r.reason && (r.changes.modified || r.changes.created || r.changes.deleted)) ?? null
    : null;
  const jobText = job ? TEXT[job.ruleId] ?? TEXT[CAB_TO_TICKETS] : null;

  const start = (ruleId: string) => run(async () => {
    setNotice(null);
    setJob(await startSyncJob(projectId, ruleId));
  }, `${TEXT[ruleId]?.failed ?? "Couldn't start the sync"}.`);

  // The run stops a few seconds after the request; show that right away, then let polling pick up "cancelled".
  const cancel = () => job && run(async () => {
    await cancelSyncJob(projectId, job.jobId);
    setJob({ ...job, cancelRequested: true });
  }, "Couldn't cancel.");

  const apply = (decisions: Record<string, SyncDecision>) => job && run(async () => {
    const result = await applySyncJob(projectId, job.jobId, decisions);
    setReviewOpen(false);
    setNotice({ text: syncAppliedText(job.ruleId, result), tone: syncAppliedAnything(result) ? 'success' : 'info' });
    onApplied?.(job.ruleId);
    await loadStatus();
  }, "Couldn't apply the changes.");

  // Nothing to change: applying an empty set records that the two sides agree, which clears the banner.
  // Changes the run could not work out stay pending, and the notice says so.
  const markSynced = () => job && run(async () => {
    const result = await applySyncJob(projectId, job.jobId, {});
    if (result.stillPending.length) setNotice({ text: syncAppliedText(job.ruleId, result), tone: 'info' });
    onApplied?.(job.ruleId);
    await loadStatus();
  }, "Couldn't mark as up to date.");

  // Discard rejects every proposed change: nothing changes, the edits count as handled, and the same changes are not
  // proposed again (only edits the run could not work out stay pending).
  const discard = () => job && run(async () => {
    const result = await applySyncJob(projectId, job.jobId, rejectAllDecisions(job));
    setReviewOpen(false);
    setNotice({ text: syncDiscardedText(job.ruleId, result), tone: 'success' });
    onApplied?.(job.ruleId);
    await loadStatus();
  }, "Couldn't discard the changes.");

  // The CAB page reviews tickets->CAB changes inline in the CAB; the pop-up is the fallback when that cannot open.
  const reviewInline = job?.ruleId === TICKETS_TO_CAB ? onReviewInline : undefined;
  const review = () => {
    if (job && reviewInline) {
      run(async () => {
        setOpening(true);
        let opened = false;
        try {
          opened = await reviewInline(job);
        } finally {
          setOpening(false);
        }
        if (opened) return;
        // The pop-up can only apply a job that is still open, and this bar is not refreshed when someone else
        // handles it. If the check itself fails (other than the job being gone), the pop-up is still the best bet.
        if ((await isSyncReviewOpen(projectId, job.jobId)) !== false) {
          setReviewOpen(true);
        } else {
          setError('These changes were already handled elsewhere.');
          await loadStatus();
        }
      }, "Couldn't open the review.");
    } else {
      setReviewOpen(true);
    }
  };

  // Sent here to review the CAB updates: once the first status read is in, open them as "Review updates" would.
  // Nothing to review any more (handled meanwhile, still running, no CAB changes) leaves the bar as it is.
  const autoReviewedRef = useRef(false);
  useEffect(() => {
    if (!autoReview || !loaded || autoReviewedRef.current) return;
    autoReviewedRef.current = true;
    if (canEdit && job?.ruleId === TICKETS_TO_CAB && job.status === 'completed' && (job.result?.proposals.length ?? 0) > 0) {
      review();
    }
    onAutoReviewDone?.();
  }, [autoReview, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const proposals = job?.result?.proposals.length ?? 0;
  const reviewWaiting = job?.status === 'completed' && proposals > 0;
  // A string, so a re-read of the same job does not report the same tickets again.
  const lockedKey = reviewWaiting ? (job?.lockedTickets ?? []).join(',') : '';
  useEffect(() => {
    onReviewWaitingChange?.(reviewWaiting, lockedKey ? lockedKey.split(',') : []);
  }, [reviewWaiting, lockedKey, onReviewWaitingChange]);
  const unhandled = (job?.result?.outcomes ?? []).filter(o => o.outcome === 'unhandled').length;
  const pendingDetails = (rule: SyncRuleStatus): string[] => {
    if (rule.ruleId === TICKETS_TO_CAB) {
      return [describeCounts(rule.changes, 'ticket')];
    }
    const cab = describeCounts(rule.changes, 'CAB item');
    return [[cab, rule.since && `Last updated: ${formatDate(rule.since)}`].filter(Boolean).join(' · ')];
  };

  return (
    <>
      {error && (
        <Bar tone="error" title={error}>
          <Button label="Dismiss" variant="outline" size="small" onClick={() => setError(null)} />
        </Bar>
      )}

      {job && jobText && jobRunning && (job.cancelRequested ? (
        <Bar tone="info" spinning title="Stopping the check…" details={['This takes a few seconds.']}>
          {canEdit && <Button label="Cancelling…" variant="outline" size="small" disabled />}
        </Bar>
      ) : (
        <Bar tone="info" spinning title={job.followupOf ? FOLLOWUP.running : jobText.running} details={['You can keep working.']}>
          {canEdit && (
            <Button label={busy ? 'Cancelling…' : 'Cancel'} variant="outline" size="small" onClick={cancel} disabled={busy} />
          )}
        </Bar>
      ))}

      {job && jobText && job.status === 'completed' && (proposals ? (
        <Bar tone="info" title={job.followupOf ? FOLLOWUP.ready(job) : jobText.ready(proposals)}
          details={[proposals === 1 ? 'Review it and decide whether to apply it.' : 'Review them and apply the ones you want.']}>
          {canEdit && (
            <Button label={opening ? 'Opening…' : 'Review updates'} variant="primary" size="small"
              onClick={review} disabled={busy} />
          )}
        </Bar>
      ) : unhandled ? (
        <Bar
          tone="info"
          title={`${plural(unhandled, 'ticket change')} could not be worked out`}
          details={['They stay pending; update the CAB again to retry them.']}
        >
          {canEdit && <Button label="See details" variant="outline" size="small" onClick={() => setReviewOpen(true)} />}
          {canEdit && (
            <Button label={busy ? 'Saving…' : 'Done'} variant="primary" size="small" onClick={markSynced} disabled={busy} />
          )}
        </Bar>
      ) : (
        <Bar tone="success" title={job.followupOf ? FOLLOWUP.none : jobText.none}>
          {canEdit && (job.result?.outcomes.length ?? 0) > 0 && (
            <Button label="See details" variant="outline" size="small" onClick={() => setReviewOpen(true)} />
          )}
          {canEdit && (
            <Button label={busy ? 'Saving…' : 'Mark as up to date'} variant="primary" size="small" onClick={markSynced} disabled={busy} />
          )}
        </Bar>
      ))}

      {job && jobText && job.status === 'failed' && (
        <Bar tone="error" title={`${job.followupOf ? FOLLOWUP.failed : jobText.failed}${job.error ? `: ${job.error}` : ''}`}>
          {canEdit && <Button label="Try again" variant="outline" size="small" onClick={() => start(job.ruleId)} disabled={busy} />}
        </Bar>
      )}

      {(!job || job.status === 'cancelled' || job.status === 'failed') && pending && (job?.status !== 'failed' || job.ruleId !== pending.ruleId) && (
        <Bar
          tone="info"
          title={TEXT[pending.ruleId]?.pending ?? 'Changes to sync'}
          details={[
            ...pendingDetails(pending),
            canEdit ? "You'll review every update before it's applied." : 'Ask a project editor to review them.',
          ].filter(Boolean)}
        >
          {canEdit && (
            <Button label={busy ? 'Starting…' : TEXT[pending.ruleId]?.start ?? 'Sync'} variant="primary" size="small"
              onClick={() => start(pending.ruleId)} disabled={busy} />
          )}
        </Bar>
      )}

      {!job && waiting && (
        <Bar
          tone="info"
          title={TEXT[waiting.ruleId]?.pending ?? 'Changes to sync'}
          details={[...pendingDetails(waiting), `Waiting: ${waiting.reason}`].filter(Boolean)}
        />
      )}

      {notice && (
        <Bar tone={notice.tone} title={notice.text}>
          <Button label="Dismiss" variant="outline" size="small" onClick={() => setNotice(null)} />
        </Bar>
      )}

      {fading && <Bar tone="success" title={fading} />}

      {job?.status === 'completed' && reviewOpen && (
        <RequirementSyncReview
          key={job.jobId}
          isOpen
          job={job}
          applying={busy}
          onApply={apply}
          onDiscard={discard}
          onClose={() => setReviewOpen(false)}
        />
      )}
    </>
  );
};

export default RequirementSyncBanner;
